<template>
	<div
		class="min-h-screen flex flex-col transition-colors duration-300 font-['Inter',sans-serif]"
		:class="isLight ? 'bg-[#e4f4f0]' : 'bg-[#0f1f1c]'">
		<NavBarComponent :isLight="isLight" />

		<!-- Imigongo background patterns -->
		<div
			class="fixed inset-0 pointer-events-none overflow-hidden"
			aria-hidden="true">
			<svg
				class="absolute -top-10 -right-10 w-[420px] h-[420px]"
				:style="{ opacity: isLight ? 0.07 : 0.06 }"
				viewBox="0 0 160 160"
				fill="none"
				xmlns="http://www.w3.org/2000/svg">
				<g :fill="isLight ? '#0d6b5e' : '#a8d5cc'">
					<rect
						x="40"
						y="0"
						width="28"
						height="28"
						transform="rotate(45 40  0)" />
					<rect
						x="80"
						y="0"
						width="28"
						height="28"
						transform="rotate(45 80  0)" />
					<rect
						x="120"
						y="0"
						width="28"
						height="28"
						transform="rotate(45 120 0)" />
					<rect
						x="20"
						y="40"
						width="28"
						height="28"
						transform="rotate(45 20  40)" />
					<rect
						x="60"
						y="40"
						width="28"
						height="28"
						transform="rotate(45 60  40)" />
					<rect
						x="100"
						y="40"
						width="28"
						height="28"
						transform="rotate(45 100 40)" />
					<rect
						x="140"
						y="40"
						width="28"
						height="28"
						transform="rotate(45 140 40)" />
					<rect
						x="40"
						y="80"
						width="28"
						height="28"
						transform="rotate(45 40  80)" />
					<rect
						x="80"
						y="80"
						width="28"
						height="28"
						transform="rotate(45 80  80)" />
					<rect
						x="120"
						y="80"
						width="28"
						height="28"
						transform="rotate(45 120 80)" />
					<rect
						x="20"
						y="120"
						width="20"
						height="20"
						transform="rotate(45 20  120)" />
					<rect
						x="60"
						y="120"
						width="20"
						height="20"
						transform="rotate(45 60  120)" />
					<rect
						x="100"
						y="120"
						width="20"
						height="20"
						transform="rotate(45 100 120)" />
					<rect
						x="140"
						y="120"
						width="20"
						height="20"
						transform="rotate(45 140 120)" />
				</g>
			</svg>
			<svg
				class="absolute -bottom-10 -left-10 w-[280px] h-[280px]"
				:style="{ opacity: isLight ? 0.06 : 0.05 }"
				viewBox="0 0 120 120"
				fill="none"
				xmlns="http://www.w3.org/2000/svg">
				<g
					:stroke="isLight ? '#0f1f1c' : '#a8d5cc'"
					stroke-width="3"
					fill="none">
					<rect
						x="60"
						y="10"
						width="64"
						height="64"
						transform="rotate(45 60 10)" />
					<rect
						x="60"
						y="22"
						width="46"
						height="46"
						transform="rotate(45 60 22)" />
					<rect
						x="60"
						y="34"
						width="28"
						height="28"
						transform="rotate(45 60 34)" />
					<rect
						x="60"
						y="46"
						width="10"
						height="10"
						transform="rotate(45 60 46)" />
				</g>
			</svg>
		</div>

		<main class="relative flex-grow pt-24 pb-20">
			<div class="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
				<!-- Page header -->
				<div class="mb-10">
					<div
						class="inline-flex items-center gap-2 mb-4 text-[11px] font-semibold tracking-widest uppercase border rounded-full px-4 py-1.5"
						:class="
							isLight
								? 'text-[#0d6b5e] bg-[#1a9b8a]/10 border-[#1a9b8a]/30'
								: 'text-[#a8d5cc] bg-[#1a9b8a]/10 border-[#1a9b8a]/25'
						">
						<span class="w-1.5 h-1.5 rounded-full bg-[#1a9b8a]"></span>
						{{ $t("contact.badge") }}
					</div>
					<h1
						class="font-['Montserrat'] font-extrabold text-[clamp(32px,5vw,52px)] leading-tight tracking-tight mb-3"
						:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
						{{ $t("contact.headline") }}
						<em
							class="not-italic"
							:class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'"
							>{{ $t("contact.highlight") }}</em
						>
					</h1>
					<p
						class="text-sm leading-relaxed"
						:class="isLight ? 'text-[#0f1f1c]/55' : 'text-white/45'">
						{{ $t("contact.descLine1") }}<br />
						{{ $t("contact.descLine2") }}
					</p>
				</div>

				<!-- Contact card -->
				<div
					class="rounded-2xl border overflow-hidden"
					:class="
						isLight
							? 'bg-white/60 border-[#1a9b8a]/15'
							: 'bg-white/[0.03] border-white/[0.07]'
					">
					<!-- Email row -->
					<a
						href="mailto:info@trekko.xyz"
						class="flex items-center gap-4 px-6 py-5 transition-all duration-200 group"
						:class="isLight ? 'hover:bg-[#1a9b8a]/5' : 'hover:bg-[#1a9b8a]/5'">
						<!-- Icon -->
						<div
							class="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-200"
							:class="
								isLight
									? 'bg-[#1a9b8a]/15 text-[#1a9b8a]'
									: 'bg-[#1a9b8a]/10 text-[#a8d5cc]'
							">
							<svg
								class="w-4.5 h-4.5"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
							</svg>
						</div>
						<div class="flex-grow">
							<p
								class="text-[11px] font-semibold tracking-widest uppercase mb-0.5"
								:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
								{{ $t("contact.emailLabel") }}
							</p>
							<p
								class="text-sm font-semibold transition-colors duration-200"
								:class="
									isLight
										? 'text-[#0f1f1c] group-hover:text-[#1a9b8a]'
										: 'text-white group-hover:text-[#a8d5cc]'
								">
								info@trekko.xyz
							</p>
						</div>
						<!-- Arrow -->
						<svg
							class="w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all duration-200 -translate-x-1 group-hover:translate-x-0"
							:class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
						</svg>
					</a>

					<!-- Divider -->
					<div
						class="mx-6 border-t"
						:class="
							isLight ? 'border-black/[0.06]' : 'border-white/[0.06]'
						"></div>

					<!-- Response time note -->
					<div class="flex items-center gap-3 px-6 py-4">
						<svg
							class="w-4 h-4 flex-shrink-0"
							:class="isLight ? 'text-[#1a9b8a]/60' : 'text-[#a8d5cc]/50'"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
						<p
							class="text-xs"
							:class="isLight ? 'text-[#0f1f1c]/45' : 'text-white/35'">
							{{ $t("contact.responseTime") }}
						</p>
					</div>
				</div>

				<!-- What we help with -->
				<div
					class="mt-6 rounded-2xl border px-6 py-5"
					:class="
						isLight
							? 'bg-white/60 border-[#1a9b8a]/15'
							: 'bg-white/[0.03] border-white/[0.07]'
					">
					<p
						class="text-[11px] font-semibold tracking-widest uppercase mb-4"
						:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
						{{ $t("contact.helpWith") }}
					</p>
					<div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
						<div
							v-for="topic in topics"
							:key="topic.labelKey"
							class="flex items-center gap-3">
							<div
								class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
								:class="
									isLight
										? 'bg-[#1a9b8a]/10 text-[#1a9b8a]'
										: 'bg-[#1a9b8a]/10 text-[#a8d5cc]'
								">
								<svg
									class="w-3.5 h-3.5"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										:d="topic.icon" />
								</svg>
							</div>
							<span
								class="text-sm"
								:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
								{{ $t(topic.labelKey) }}
							</span>
						</div>
					</div>
				</div>
			</div>
		</main>

		<FooterComponent :isLight="isLight" @toggle-mode="toggleMode" />
	</div>
</template>

<script>
import NavBarComponent from "@/components/NavBarComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import { useTheme } from "@/composables/useTheme";

export default {
	components: {
		NavBarComponent,
		FooterComponent,
	},
	setup() {
		const { isLight, toggleMode } = useTheme();
		return { isLight, toggleMode };
	},
	data() {
		return {
			topics: [
				{
					labelKey: "contact.topics.tickets",
					icon: "M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z",
				},
				{
					labelKey: "contact.topics.events",
					icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
				},
				{
					labelKey: "contact.topics.partnerships",
					icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
				},
				{
					labelKey: "contact.topics.general",
					icon: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
				},
			],
		};
	},
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&family=Inter:wght@300;400;500&display=swap");
</style>
